import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useIndexerPaths } from '../../lib/indexer-paths';
import { getIndexerErrorInfo } from '../../api/errors';
import { useLazyGetProjectAdvancedStatsQuery } from '../../api/indexerApi';
import type { ProjectAdvancedStatsData } from '../../api/types';
import {
  advancedStatsDays,
  fillTrendDays,
  segmentHttpChartData,
  segmentIndexChartData,
  trendChartData,
} from '../../lib/advanced-stats';
import {
  inspectBucketBarSeries,
  segmentHttpBarSeries,
  segmentIndexBarSeries,
} from '../../lib/chart-colors';
import { changesPath, urlsPath } from '../../lib/drilldown-links';
import type { StatsRange } from '../../lib/job-range';
import { formatFaNumber } from '../../lib/utils';
import {
  ComparisonHeroCards,
  ComparisonTable,
} from './ComparisonPanel';
import { HorizontalSegmentChart } from './HorizontalSegmentChart';
import { PageChartsPanel } from './PageChartsPanel';
import { StackedBarChart } from './StackedBarChart';
import { IndexerChartCard } from '../IndexerChartCard';
import { OverviewComparisonPreload } from '../preloads/IndexerPreloads';

export function OverviewAdvancedCharts({
  projectId,
  rangeDays,
  embedded = false,
}: {
  projectId: string;
  rangeDays: StatsRange;
  /** بدون پنل تاشو — برای تب مقایسه در نمای کلی */
  embedded?: boolean;
}) {
  const paths = useIndexerPaths();
  const [stats, setStats] = useState<ProjectAdvancedStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [getProjectAdvancedStats] = useLazyGetProjectAdvancedStatsQuery();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectAdvancedStats(
        { projectId, days: rangeDays },
        true,
      ).unwrap();
      setStats(data);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message || 'بارگذاری آمار پیشرفته ناموفق بود',
      );
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, rangeDays, getProjectAdvancedStats]);

  useEffect(() => {
    void load();
  }, [load]);

  const advancedDays = advancedStatsDays(rangeDays);

  const trendData = useMemo(() => {
    if (!stats) return [];
    return trendChartData(fillTrendDays(stats.trends, advancedDays));
  }, [stats, advancedDays]);

  const segmentIndexData = useMemo(
    () => (stats ? segmentIndexChartData(stats.segmentsIndex) : []),
    [stats],
  );
  const segmentHttpData = useMemo(
    () => (stats ? segmentHttpChartData(stats.segmentsHttp) : []),
    [stats],
  );

  const comparison = stats?.comparison;

  const body = (
    <>
      {error && (
        <Alert
          variant="danger"
          className={embedded ? 'mb-3' : 'mx-3 mt-3 mb-0'}
          dismissible
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {loading && !stats ? (
        <OverviewComparisonPreload />
      ) : stats && comparison ? (
        <div className="d-flex flex-column gap-3">
          {embedded && (
            <p className="text-secondary small mb-0">
              {advancedDays === 1
                ? 'مقایسهٔ امروز با دیروز'
                : `مقایسهٔ ${formatFaNumber(advancedDays)} روز اخیر با ${formatFaNumber(advancedDays)} روز قبل`}
            </p>
          )}
          <ComparisonHeroCards
            cards={[
              {
                label: 'نتایج ایندکس‌شده',
                guideKey: 'comparison-hero-indexed',
                current: comparison.current.inspect.indexed,
                previous: comparison.previous.inspect.indexed,
                tone: 'success',
                href: urlsPath({ status: 'indexed', days: advancedDays , base: paths.urls }),
              },
              {
                label: 'نتایج ایندکس‌نیست',
                guideKey: 'comparison-hero-excluded',
                current: comparison.current.inspect.excluded,
                previous: comparison.previous.inspect.excluded,
                invert: true,
                tone: 'warning',
                href: urlsPath({ status: 'not_indexed', days: advancedDays , base: paths.urls }),
              },
              {
                label: 'تغییر: ایندکس از دست رفت',
                guideKey: 'comparison-hero-lost',
                current: comparison.current.changes.becameNotIndexed,
                previous: comparison.previous.changes.becameNotIndexed,
                invert: true,
                tone: 'danger',
                href: changesPath({ filter: 'became_not_indexed', days: advancedDays, base: paths.changes }),
              },
            ]}
          />

          <div className="row g-3">
            <div className="col-lg-6">
              <ComparisonTable
                title="مقایسهٔ بازرسی"
                subtitle="نتایج بازرسی این دوره نسبت به دورهٔ قبل"
                guideKey="comparison-inspect"
                rows={[
                  {
                    label: 'ایندکس‌شده',
                    tone: 'success',
                    current: comparison.current.inspect.indexed,
                    previous: comparison.previous.inspect.indexed,
                    href: urlsPath({ status: 'indexed', days: advancedDays , base: paths.urls }),
                  },
                  {
                    label: 'ایندکس‌نیست',
                    tone: 'warning',
                    current: comparison.current.inspect.excluded,
                    previous: comparison.previous.inspect.excluded,
                    invertEvolution: true,
                    href: urlsPath({
                      status: 'not_indexed',
                      days: advancedDays,
                      base: paths.urls,
                    }),
                  },
                  {
                    label: 'خطا',
                    tone: 'danger',
                    current: comparison.current.inspect.error,
                    previous: comparison.previous.inspect.error,
                    invertEvolution: true,
                    href: urlsPath({ status: 'error', days: advancedDays , base: paths.urls }),
                  },
                ]}
              />
            </div>
            <div className="col-lg-6">
              <ComparisonTable
                title="مقایسهٔ تغییرات وضعیت"
                subtitle="آدرس‌هایی که وضعیت ایندکس‌شان عوض شده"
                guideKey="comparison-changes"
                footer={
                  <Link to={paths.changes} className="btn btn-link btn-sm p-0">
                    مشاهدهٔ لاگ
                  </Link>
                }
                rows={[
                  {
                    label: 'ایندکس شد',
                    tone: 'success',
                    current: comparison.current.changes.becameIndexed,
                    previous: comparison.previous.changes.becameIndexed,
                    href: changesPath({
                      filter: 'became_indexed',
                      days: advancedDays,
                      base: paths.changes,
                    }),
                  },
                  {
                    label: 'ایندکس از دست رفت',
                    tone: 'danger',
                    current: comparison.current.changes.becameNotIndexed,
                    previous: comparison.previous.changes.becameNotIndexed,
                    invertEvolution: true,
                    href: changesPath({
                      filter: 'became_not_indexed',
                      days: advancedDays,
                      base: paths.changes,
                    }),
                  },
                  {
                    label: 'خطا',
                    tone: 'danger',
                    current: comparison.current.changes.error,
                    previous: comparison.previous.changes.error,
                    invertEvolution: true,
                    href: changesPath({ filter: 'error', days: advancedDays , base: paths.changes }),
                  },
                ]}
              />
            </div>
          </div>

          <IndexerChartCard
            title="روند وضعیت ایندکس در بازرسی‌ها"
            subtitle="تعداد نتایج بازرسی روزانه بر اساس bucket (ایندکس‌شده / ایندکس‌نیست / خطا)"
            guideKey="trend-inspect-buckets"
          >
            <StackedBarChart
              data={trendData}
              height={300}
              series={inspectBucketBarSeries()}
            />
          </IndexerChartCard>

          <div className="row g-3">
            <div className="col-lg-6">
              <IndexerChartCard
                title="وضعیت ایندکس بر اساس مسیر"
                subtitle="سگمنت اول مسیر URL برای آدرس‌های بازرسی‌شده در بازه"
                guideKey="segment-index-by-path"
              >
                <HorizontalSegmentChart
                  data={segmentIndexData}
                  series={segmentIndexBarSeries()}
                />
              </IndexerChartCard>
            </div>
            <div className="col-lg-6">
              <IndexerChartCard
                title="کدهای HTTP بر اساس مسیر"
                subtitle="آخرین بازرسی هر URL در بازهٔ انتخاب‌شده"
                guideKey="segment-http-by-path"
              >
                <HorizontalSegmentChart
                  data={segmentHttpData}
                  series={segmentHttpBarSeries()}
                />
              </IndexerChartCard>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="overview-tab-panel">{body}</div>;
  }

  return (
    <PageChartsPanel
      title="روند، مقایسه و سگمنت"
      subtitle={
        advancedDays === 1
          ? 'مقایسهٔ امروز با دیروز'
          : `مقایسهٔ ${formatFaNumber(advancedDays)} روز اخیر با ${formatFaNumber(advancedDays)} روز قبل`
      }
      guideKey="advanced-comparison-panel"
    >
      {body}
    </PageChartsPanel>
  );
}
